import React from "react";

// ── Compliance Badge ──────────────────────────────────────────────────────────
export const ComplianceBadge = ({ label, isCompliant }) => (
  <div
    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-medium ${
      isCompliant
        ? "bg-green-500/10 text-green-400 border border-green-500/20"
        : "bg-gray-600/50 text-gray-400 border border-gray-600"
    }`}
  >
    {isCompliant ? (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    ) : (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
    )}
    {label}
  </div>
);

// ── Product Card ──────────────────────────────────────────────────────────────
function ProductCard({ product, index, language, onView, onEdit, onDelete }) {
  return (
    <div
      className="flex flex-col justify-between group relative bg-white border border-gray-200 rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/10 hover:-translate-y-1"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Hover gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none" />

      {/* Product Header */}
      <div className="relative flex justify-between items-start mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-gray-900 truncate group-hover:text-orange-500 transition-colors uppercase">
                {product.model_id || product.name}
              </h3>
              <p className="text-sm text-gray-500 truncate capitalize">
                {product.category}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Product Details Grid */}
      <div className="space-y-3 mb-4">
        {/* Category */}
        <div className="flex items-start gap-2">
          <svg
            className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
            />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500">
              {language === "zh" ? "產品類別" : "Category"}
            </p>
            <p className="text-sm text-gray-900 capitalize">
              {[
                product.category_name,
                product.subcategory_name,
                product.subsubcategory_name,
              ]
                .filter(Boolean)
                .join(" › ") ||
                (language === "zh" ? "未分類" : "Uncategorized")}
            </p>
          </div>
        </div>

        {/* PDF URL */}
        {product.pdf_url && (
          <div className="flex items-start gap-2">
            <svg
              className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">
                {language === "zh" ? "PDF 文件" : "PDF Document"}
              </p>
              <a
                href={product.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-orange-500 hover:text-orange-600 truncate block hover:underline"
              >
                {language === "zh" ? "查看PDF" : "View PDF"}
              </a>
            </div>
          </div>
        )}

        {/* Description */}
        {product.description && product.description.trim() !== "" && (
          <div className="flex items-start gap-2">
            <svg
              className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h7"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">
                {language === "zh" ? "描述" : "Description"}
              </p>
              <p className="text-sm text-gray-700 line-clamp-2">
                {product.description}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Compliance Badges */}
      <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-gray-200">
        {product.rohs_compliant && (
          <ComplianceBadge label="RoHS" isCompliant={product.rohs_compliant} />
        )}
        {product.reach_compliant && (
          <ComplianceBadge
            label="REACH"
            isCompliant={product.reach_compliant}
          />
        )}
        {product.ul_comparable && (
          <ComplianceBadge label="UL" isCompliant={product.ul_comparable} />
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => onView(product.model_id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-300 text-sm font-medium shadow-lg shadow-orange-500/30"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          {language === "zh" ? "完整屬性" : "Full Details"}
        </button>
        {/* <button
                    onClick={() => onEdit(product)}
                    className="p-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-300 shadow-lg shadow-blue-500/30"
                    title={language === "zh" ? "編輯產品" : "Edit Product"}
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                    </svg>
                </button> */}
        <button
          onClick={() => onDelete(product.product_id)}
          className="p-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-300 shadow-lg shadow-red-500/30"
          title={language === "zh" ? "刪除產品" : "Delete Product"}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default ProductCard;
